import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';

import createProject from 'api/project/createProject';
import useModal from 'hooks/useModal';
import useUser from 'hooks/useUser';
import { projectInfoSchema } from 'schemas/projectInfoSchema';
import { ProjectInfoFormValues } from 'types';
import useProjects from 'pages/MyProject/hooks/useProjects';

const useCreateProject = () => {
  const modal = useModal();
  const { data: userData } = useUser();
  const [step, setStep] = useState(1);
  const { refetch } = useProjects();

  const useFormReturn = useForm<ProjectInfoFormValues>({
    resolver: yupResolver(projectInfoSchema),
    defaultValues: {
      framework: 'PyTorch',
      direction: 'vertical',
    },
  });

  const createProjectMutation = useMutation({
    mutationFn: createProject,
    onSuccess: (_, values) => {
      modal.close();
      refetch();
      window.open(`/workspace/${userData?.userName}/${values.projectName}`, '_blank');
    },
  });

  const onValid = (values: ProjectInfoFormValues) => {
    createProjectMutation.mutate({
      projectName: values.projectName,
      framework: values.framework,
      frameworkVersion: values.frameworkVersion,
      direction: values.direction,
      template: values.template,
    });
  };

  const handlePrevClick = () => {
    setStep((prev) => prev - 1);
  };

  const handleTemplateClick = (templateId: string) => {
    useFormReturn.setValue('template', templateId);
    setStep((prev) => prev + 1);
  };

  return {
    step,
    isLoading: createProjectMutation.isPending || useFormReturn.formState.isSubmitting,
    useFormReturn,
    onValid,
    onPrevClick: handlePrevClick,
    onTemplateClick: handleTemplateClick,
  };
};

export default useCreateProject;
